import { zodResolver } from "@hookform/resolvers/zod";
import { MouseEventHandler, use, useEffect } from "react";
import { Control, Resolver, useForm } from "react-hook-form";
import { useParams } from "react-router";
import {
  CreateProjectRequest,
  ManageProjectRequest,
} from "tng-address-collection-shared/types/apiTypes.ts";
import { fallbackProject } from "../defaults/fallbackProject.tsx";
import { NotUniqueError } from "../hooks/errors.ts";
import { useEditProjectPermissions } from "../hooks/useEditProjectPermissions.ts";
import { useGetManageProjectData } from "../hooks/useGetManageProjectData.ts";
import { useTranslator } from "../i18n/translator.ts";
import { SnackbarContext } from "../snackbar/SnackbarContext.ts";
import { FormContainer } from "./FormContainer.tsx";
import { ProjectFormFields } from "./ProjectFormFields.tsx";

export const ManageProjectDialog = ({ onClose }: { onClose: () => void }) => {
  const { projectId } = useParams();
  const { project, isFetching } = useGetManageProjectData(projectId ?? "");

  const { displayError, displaySuccess } = use(SnackbarContext);
  const translate = useTranslator();
  const { mutate: editProject, isPending } = useEditProjectPermissions();

  const { control, handleSubmit, reset } = useForm({
    resolver: zodResolver(
      ManageProjectRequest,
    ) as Resolver<ManageProjectRequest>,
    defaultValues: project ?? fallbackProject,
  });

  useEffect(() => {
    reset(project);
  }, [project, reset]);

  const onSubmit = (formData: ManageProjectRequest) => {
    editProject(formData, {
      onSuccess: () => {
        displaySuccess(translate("changesSaved"));
        onClose();
      },
      onError: (e) => {
        if (e instanceof NotUniqueError) {
          displayError(translate("errorProjectNameNotUnique"));
        } else {
          displayError(translate("errorSavingChanges"));
        }
      },
    });
  };

  return (
    <FormContainer
      title={translate("manageProjectPermissions")}
      onClose={onClose}
      isLoading={!projectId || isFetching}
      saveConfig={{
        onSave: handleSubmit(onSubmit) as MouseEventHandler,
        isSaveDisabled: isPending,
      }}
    >
      <ProjectFormFields
        control={
          control as Control<ManageProjectRequest | CreateProjectRequest>
        }
      />
    </FormContainer>
  );
};
